// Client-side image transcoding to WebP. Job-photo upload paths only —
// not compliance docs (PDFs), not workspace logos (SVG + tiny PNGs).
//
// Why: the capacity audit found photos averaging ~3.6 MB on PROD. At the
// projected 250 jobs/day with even 25% photo'd jobs, storage trajectory
// crosses Supabase's 100 GB tier in ~4 months. WebP at ~0.8 quality
// typically cuts JPEG/HEIC by 70–85%, dropping the storage curve to
// ~100 GB / yr. Bonus: fixes the known HEIC-thumbnail-broken-on-
// Chrome/Firefox issue called out in JobPhotoStrip.tsx's comment.
//
// Contract: NEVER throw, NEVER block the upload. Transcode is best-
// effort — any failure path returns the original File so the upload
// flow always proceeds with SOMETHING. Console.warn on fallback for
// debugging; no toast (silent to the user — they uploaded a photo,
// they got a photo, whether it was optimized is not their concern).
//
// Cases:
//   * image/webp                       → returned unchanged (already optimal)
//   * image/jpeg, image/png            → canvas.toBlob('image/webp', 0.8)
//   * image/heic, image/heif           → dynamic-import heic-to → JPEG blob → canvas → WebP
//   * any other / unknown / corrupt    → returned unchanged
//   * WebP output >= original size     → returned unchanged (rare on
//                                        already-compressed small images)
//
// Bundle: heic-to is imported dynamically inside the HEIC branch so it
// becomes a Vite chunk that loads only when an iOS user uploads a HEIC.
// JPEG/PNG-only sessions never pay the ~120 KB cost.

const WEBP_QUALITY = 0.8

/**
 * Best-effort transcode to WebP. Always resolves; never rejects.
 * On any failure or non-beneficial result, returns the input file
 * unchanged so the caller can upload it as-is.
 */
export async function transcodeImageToWebP(file: File): Promise<File> {
  // Fast paths first — no work needed.
  if (file.type === 'image/webp') return file

  try {
    let decodableBlob: Blob = file

    if (file.type === 'image/heic' || file.type === 'image/heif') {
      // Dynamic import — keeps heic-to OUT of the main bundle. Vite emits
      // it as a separate chunk loaded on first HEIC encounter.
      const mod = await import('heic-to')
      // heic-to exports `heicTo({ blob, type, quality })`. Convert HEIC → JPEG
      // blob first so the standard canvas decode path works downstream.
      decodableBlob = (await mod.heicTo({
        blob: file,
        type: 'image/jpeg',
        quality: 0.92, // intermediate quality — we recompress to WebP next
      })) as Blob
    } else if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      // Unknown / unsupported source — bail.
      console.warn('[transcodeImageToWebP] unsupported type, passing through:', file.type)
      return file
    }

    // Decode via an <img> — modern browsers honor EXIF orientation on
    // decode for JPEG, so the canvas draw inherits the correct rotation
    // without us re-reading EXIF ourselves.
    const objectUrl = URL.createObjectURL(decodableBlob)
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image()
        el.onload = () => resolve(el)
        el.onerror = () => reject(new Error('image decode failed'))
        el.src = objectUrl
      })

      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        console.warn('[transcodeImageToWebP] no 2d context, passing through')
        return file
      }
      ctx.drawImage(img, 0, 0)

      const webpBlob = await new Promise<Blob | null>(resolve => {
        canvas.toBlob(b => resolve(b), 'image/webp', WEBP_QUALITY)
      })

      if (!webpBlob) {
        console.warn('[transcodeImageToWebP] toBlob returned null, passing through')
        return file
      }

      // Defensive: if WebP came out LARGER than the source (rare on
      // already-compressed small images — common when the source is
      // a thumbnail JPEG), upload the original instead.
      if (webpBlob.size >= file.size) {
        console.warn('[transcodeImageToWebP] WebP output not smaller, passing through:',
          `webp=${webpBlob.size} orig=${file.size}`)
        return file
      }

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo'
      return new File([webpBlob], `${baseName}.webp`, { type: 'image/webp' })
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  } catch (err) {
    console.warn('[transcodeImageToWebP] fallback to original:', err)
    return file
  }
}
